import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Phone, Copy, Check, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/copy")({
  head: () => ({
    meta: [
      { title: "Copiar contacto — Routify" },
      { name: "description", content: "Copia um número de telefone ou contacto para a área de transferência." },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    text: typeof search.text === "string" ? search.text : "",
    label: typeof search.label === "string" ? search.label : "",
  }),
  component: CopyPage,
});

function CopyPage() {
  const { text, label } = Route.useSearch();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          toast.success("Número copiado com sucesso");
        })
        .catch(() => {
          setError(true);
          toast.error("Não foi possível copiar automaticamente");
        });
    } else {
      setError(true);
      toast.error("Não foi possível copiar automaticamente");
    }
  }, [text]);

  const copyAgain = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setError(false);
      toast.success("Número copiado com sucesso");
    } catch {
      setError(true);
      toast.error("Não foi possível copiar automaticamente");
    }
  };

  const display = text || "—";
  const title = label || "Número de telefone";

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-6 rounded-full bg-primary/10 p-4">
        {copied ? (
          <Check className="h-8 w-8 text-primary" />
        ) : (
          <Phone className="h-8 w-8 text-primary" />
        )}
      </div>
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {copied ? "Copiado para a área de transferência." : error ? "Não foi possível copiar automaticamente." : "A copiar..."}
      </p>

      <div className="mt-6 w-full rounded-xl border border-border bg-card p-4">
        <p className="select-all text-xl font-medium tracking-wide">{display}</p>
      </div>

      <div className="mt-6 flex w-full flex-col gap-3">
        <Button onClick={copyAgain} className="w-full gap-2" disabled={!text}>
          <Copy className="h-4 w-4" />
          Copiar novamente
        </Button>
        {text && (
          <Button variant="outline" asChild className="w-full gap-2">
            <a href={`tel:${text.replace(/\s+/g, "")}`}>
              <Phone className="h-4 w-4" />
              Ligar
            </a>
          </Button>
        )}
        <Button variant="ghost" asChild className="w-full gap-2">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Voltar ao Routify
          </Link>
        </Button>
      </div>
    </main>
  );
}
